import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  Divider,
  Stack } from
'@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  LocationOn as LocationIcon,
  Devices as DeviceIcon,
  Person as UserIcon,
  BugReport as TicketIcon,
  AccessTime as TimeIcon } from
'@mui/icons-material';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel } from
'@mui/material';
import { formatDateTime } from '../../utils/dateUtils';
import apiClient from '../../services/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';import { useThemeSettings } from "../../contexts/ThemeContext";

const LogsTable = ({
  locationId,
  deviceId,
  userId,
  ticketId,
  limit = 50
}) => {const { t } = useThemeSettings();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [timeRange, setTimeRange] = useState('24h');
  const isMobile = useIsMobile();

  //console.log('LogsTable props:', { locationId, deviceId, userId, ticketId, limit });

  useEffect(() => {
    fetchLogs();
  }, [locationId, deviceId, userId, ticketId, timeRange]);

  useEffect(() => {
    // Filter logs based on search term
    const filtered = logs.filter((log) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        log.action?.toLowerCase().includes(searchLower) ||
        log.message?.toLowerCase().includes(searchLower) ||
        log.details?.toLowerCase().includes(searchLower) ||
        log.user?.toLowerCase().includes(searchLower) ||
        log.level?.toLowerCase().includes(searchLower));

    });
    setFilteredLogs(filtered);
  }, [logs, searchTerm]);

  const getTimeRangeParams = () => {
    const now = new Date();
    let startDate = null;

    switch (timeRange) {
      case '1h':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6h':
        startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '12h':
        startDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        break;
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'all':
        startDate = null;
        break;
      default:
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    return startDate ? { startDate: startDate.toISOString(), endDate: now.toISOString() } : {};
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      //console.log('Fetching logs with params:', { locationId, deviceId, userId, ticketId, limit, timeRange });

      let response;
      const timeParams = getTimeRangeParams();
      const params = {
        limit: timeRange === 'all' ? 1000 : limit,
        ...timeParams
      };

      // Use specific endpoint based on filter
      if (locationId) {
        //console.log('Fetching logs for location:', locationId);
        response = await apiClient.logs.getByLocation(locationId, params);
      } else if (deviceId) {
        //console.log('Fetching logs for device:', deviceId);
        response = await apiClient.logs.getByDevice(deviceId, params);
      } else if (userId) {
        //console.log('Fetching logs for user:', userId);
        response = await apiClient.logs.getByUser(userId, params);
      } else if (ticketId) {
        //console.log('Fetching logs for ticket:', ticketId);
        response = await apiClient.logs.getByTicket(ticketId, params);
      } else {
        //console.log('Fetching all logs');
        response = await apiClient.logs.getLogs(params);
      }

      //console.log('Logs fetch response:', response);

      if (response.data?.success) {
        setLogs(response.data.data || []);
      } else {
        // If API doesn't return logs yet, use mock data
        setLogs(getMockLogs());
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      // Use mock data as fallback
      setLogs(getMockLogs());
    } finally {
      setLoading(false);
    }
  };

  const getMockLogs = () => {
    const now = Date.now();
    const mockLogs = [
    {
      id: 1,
      timestamp: new Date(now - 3600000).toISOString(), // 1 hour ago
      level: 'info',
      action: 'location.update',
      message: 'Location settings updated',
      user: 'John Doe',
      details: 'Modified location configuration',
      entityType: 'location',
      entityId: locationId
    },
    {
      id: 2,
      timestamp: new Date(now - 7200000).toISOString(), // 2 hours ago
      level: 'success',
      action: 'device.online',
      message: 'Device came online',
      user: 'System',
      details: 'Device reconnected to network',
      entityType: 'device',
      entityId: deviceId
    },
    {
      id: 3,
      timestamp: new Date(now - 10800000).toISOString(), // 3 hours ago
      level: 'warning',
      action: 'device.offline',
      message: 'Device went offline',
      user: 'System',
      details: 'Lost connection to device',
      entityType: 'device',
      entityId: deviceId
    },
    {
      id: 4,
      timestamp: new Date(now - 21600000).toISOString(), // 6 hours ago
      level: 'info',
      action: 'ticket.create',
      message: 'New ticket created',
      user: 'Jane Smith',
      details: 'Ticket #TKT-001 created for network issue',
      entityType: 'ticket',
      entityId: 'TKT-001'
    },
    {
      id: 5,
      timestamp: new Date(now - 86400000).toISOString(), // 1 day ago
      level: 'error',
      action: 'auth.failed',
      message: 'Authentication failed',
      user: 'Unknown',
      details: 'Multiple failed login attempts detected',
      entityType: 'security',
      entityId: null
    },
    {
      id: 6,
      timestamp: new Date(now - 172800000).toISOString(), // 2 days ago
      level: 'info',
      action: 'location.create',
      message: 'New location created',
      user: 'Admin User',
      details: 'Added new floor to building',
      entityType: 'location',
      entityId: locationId
    },
    {
      id: 7,
      timestamp: new Date(now - 604800000).toISOString(), // 7 days ago
      level: 'warning',
      action: 'device.maintenance',
      message: 'Device maintenance required',
      user: 'System',
      details: 'Firmware update available',
      entityType: 'device',
      entityId: deviceId
    },
    {
      id: 8,
      timestamp: new Date(now - 2592000000).toISOString(), // 30 days ago
      level: 'success',
      action: 'ticket.resolved',
      message: 'Ticket resolved',
      user: 'Bob Smith',
      details: 'Issue resolved successfully',
      entityType: 'ticket',
      entityId: 'TKT-000'
    },
    {
      id: 9,
      timestamp: new Date(now - 7776000000).toISOString(), // 90 days ago
      level: 'info',
      action: 'system.update',
      message: 'System update completed',
      user: 'System',
      details: 'Version upgraded to 2.0.0',
      entityType: 'system',
      entityId: null
    }];


    // Filter by time range
    const timeParams = getTimeRangeParams();
    let filteredByTime = mockLogs;

    if (timeParams.startDate) {
      const startTime = new Date(timeParams.startDate).getTime();
      filteredByTime = mockLogs.filter((log) => {
        const logTime = new Date(log.timestamp).getTime();
        return logTime >= startTime;
      });
    }

    // Filter mock logs based on current context
    if (locationId) {
      return filteredByTime.filter((log) =>
      log.entityType === 'location' ||
      log.action.includes('location')
      );
    }
    if (deviceId) {
      return filteredByTime.filter((log) =>
      log.entityType === 'device' ||
      log.action.includes('device')
      );
    }
    if (ticketId) {
      return filteredByTime.filter((log) =>
      log.entityType === 'ticket' ||
      log.action.includes('ticket')
      );
    }

    return filteredByTime;
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const params = {};

      if (locationId) {
        params.locationId = locationId;
      } else if (deviceId) {
        params.deviceId = deviceId;
      } else if (userId) {
        params.userId = userId;
      } else if (ticketId) {
        params.ticketId = ticketId;
      }

      const response = await apiClient.logs.exportLogs(params);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error exporting logs:', err);
      setError('Failed to export logs');
    }
  };

  const getLevelChip = (level) => {const { t } = useThemeSettings();
    const levelConfig = {
      'info': { color: 'info', icon: <InfoIcon /> },
      'success': { color: 'success', icon: <SuccessIcon /> },
      'warning': { color: 'warning', icon: <WarningIcon /> },
      'error': { color: 'error', icon: <ErrorIcon /> }
    };

    const config = levelConfig[level?.toLowerCase()] || levelConfig['info'];

    return (
      <Chip
        icon={config.icon}
        label={level?.toUpperCase() || 'INFO'}
        color={config.color}
        size="small"
        variant="outlined" />);


  };

  const getActionIcon = (action) => {const { t } = useThemeSettings();
    if (action?.includes('location')) return <LocationIcon fontSize="small" />;
    if (action?.includes('device')) return <DeviceIcon fontSize="small" />;
    if (action?.includes('ticket')) return <TicketIcon fontSize="small" />;
    if (action?.includes('user') || action?.includes('auth')) return <UserIcon fontSize="small" />;
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>);

  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={handleRefresh}>{t ? t("LogsTable.retry") : "Retry"}

        </Button>
        }>
          {error}
        </Alert>
      </Box>);

  }

  if (!logs || logs.length === 0) {
    return (
      <Box p={2}>
        <Alert
          severity="info"
          action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
              <RefreshIcon sx={{ mr: 1 }} />{t ? t("DeviceStatusDashboard.refresh") : "Refresh"}

          </Button>
          }>{t ? t("LogsTable.no_activity_logs_found") : "No activity logs found"}


        </Alert>
      </Box>);

  }

  // Mobile Card View
  const renderMobileView = () =>
  <Box>
      {filteredLogs.map((log) =>
    <Card key={log.id} sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              {/* Time and Level */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(log.timestamp, 'MMM dd HH:mm')}
                </Typography>
                {getLevelChip(log.level)}
              </Stack>

              <Divider />

              {/* Action */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("LogsTable.action") : "Action"}

            </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  {getActionIcon(log.action)}
                  <Typography variant="body2">
                    {log.action}
                  </Typography>
                </Box>
              </Box>

              {/* Message */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("LogsTable.message") : "Message"}

            </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {log.message}
                </Typography>
              </Box>

              {/* User */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("LogsTable.user") : "User"}

            </Typography>
                <Typography
              variant="body2"
              sx={{ mt: 0.5 }}
              color={log.user === 'System' ? 'text.secondary' : 'text.primary'}>
              
                  {log.user || 'Unknown'}
                </Typography>
              </Box>

              {/* Details */}
              {log.details &&
          <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("LogsTable.details") : "Details"}

            </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {log.details}
                  </Typography>
                </Box>
          }
            </Stack>
          </CardContent>
        </Card>
    )}

      {filteredLogs.length === 0 &&
    <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t ? t("LogsTable.no_logs_found") : "No logs found"}</Typography>
        </Paper>
    }
    </Box>;


  if (isMobile) {
    return (
      <Box>
        {/* Search and Actions Bar */}
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            placeholder={t ? t("LogsTable.search_logs") : "Search logs..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>

            }}
            fullWidth />
          
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ flexGrow: 1 }}>
              <InputLabel id="time-range-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon fontSize="small" />{t ? t("LogsTable.time_range") : "Time Range"}

                </Box>
              </InputLabel>
              <Select
                labelId="time-range-label"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label={t ? t("LogsTable.time_range") : "Time Range"}>
                
                <MenuItem value="1h">{t ? t("LogsTable.last_hour") : "Last Hour"}</MenuItem>
                <MenuItem value="6h">{t ? t("LogsTable.last_6_hours") : "Last 6 Hours"}</MenuItem>
                <MenuItem value="12h">{t ? t("LogsTable.last_12_hours") : "Last 12 Hours"}</MenuItem>
                <MenuItem value="24h">{t ? t("LogsTable.last_24_hours") : "Last 24 Hours"}</MenuItem>
                <MenuItem value="7d">{t ? t("LogsTable.last_7_days") : "Last 7 Days"}</MenuItem>
                <MenuItem value="30d">{t ? t("LogsTable.last_30_days") : "Last 30 Days"}</MenuItem>
                <MenuItem value="90d">{t ? t("LogsTable.last_90_days") : "Last 90 Days"}</MenuItem>
                <MenuItem value="all">{t ? t("LogsTable.all_time") : "All Time"}</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={t ? t("LogsTable.export_logs") : "Export Logs"}>
              <IconButton onClick={handleExport} size="small">
                <ExportIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t ? t("DeviceStatusDashboard.refresh") : "Refresh"}>
              <IconButton onClick={handleRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {renderMobileView()}

        {/* Results Summary */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{t ? t("LogsTable.showing") : "Showing"}
            {filteredLogs.length}{t ? t("LogsTable.text_6") : "of"}{logs.length}{t ? t("LogsTable.log_entries") : "log entries"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`${logs.filter((l) => l.level === 'error').length} Errors`}
              size="small"
              color="error"
              variant="outlined" />
            
            <Chip
              label={`${logs.filter((l) => l.level === 'warning').length} Warnings`}
              size="small"
              color="warning"
              variant="outlined" />
            
            <Chip
              label={`${logs.filter((l) => l.level === 'info').length} Info`}
              size="small"
              color="info"
              variant="outlined" />
            
          </Stack>
        </Box>
      </Box>);

  }

  return (
    <Box>
      {/* Search and Actions Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t ? t("LogsTable.search_logs") : "Search logs..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment:
            <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>

          }}
          sx={{ flexGrow: 1, maxWidth: 300 }} />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon fontSize="small" />{t ? t("LogsTable.time_range") : "Time Range"}

            </Box>
          </InputLabel>
          <Select
            labelId="time-range-label"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label={t ? t("LogsTable.time_range") : "Time Range"}>
            
            <MenuItem value="1h">{t ? t("LogsTable.last_hour") : "Last Hour"}</MenuItem>
            <MenuItem value="6h">{t ? t("LogsTable.last_6_hours") : "Last 6 Hours"}</MenuItem>
            <MenuItem value="12h">{t ? t("LogsTable.last_12_hours") : "Last 12 Hours"}</MenuItem>
            <MenuItem value="24h">{t ? t("LogsTable.last_24_hours") : "Last 24 Hours"}</MenuItem>
            <MenuItem value="7d">{t ? t("LogsTable.last_7_days") : "Last 7 Days"}</MenuItem>
            <MenuItem value="30d">{t ? t("LogsTable.last_30_days") : "Last 30 Days"}</MenuItem>
            <MenuItem value="90d">{t ? t("LogsTable.last_90_days") : "Last 90 Days"}</MenuItem>
            <MenuItem value="all">{t ? t("LogsTable.all_time") : "All Time"}</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title={t ? t("LogsTable.export_logs") : "Export Logs"}>
          <IconButton onClick={handleExport} size="small">
            <ExportIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t ? t("DeviceStatusDashboard.refresh") : "Refresh"}>
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table size="small" aria-label={t ? t("LogsTable.logs_table") : "logs table"}>
          <TableHead>
            <TableRow>
              <TableCell>{t ? t("LogsTable.time") : "Time"}</TableCell>
              <TableCell>{t ? t("LogsTable.level") : "Level"}</TableCell>
              <TableCell>{t ? t("LogsTable.action") : "Action"}</TableCell>
              <TableCell>{t ? t("LogsTable.message") : "Message"}</TableCell>
              <TableCell>{t ? t("LogsTable.user") : "User"}</TableCell>
              <TableCell>{t ? t("LogsTable.details") : "Details"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) =>
            <TableRow key={log.id} hover>
                <TableCell>
                  <Tooltip title={formatDateTime(log.timestamp)}>
                    <Typography variant="body2">
                      {formatDateTime(log.timestamp, 'MMM dd HH:mm')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {getLevelChip(log.level)}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getActionIcon(log.action)}
                    <Typography variant="body2">
                      {log.action}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {log.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={log.user === 'System' ? 'text.secondary' : 'text.primary'}>
                    {log.user || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={log.details || 'No details'}>
                    <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      maxWidth: 200,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    
                      {log.details || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Results Summary */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t ? t("LogsTable.showing") : "Showing"}
          {filteredLogs.length}{t ? t("LogsTable.text_6") : "of"}{logs.length}{t ? t("LogsTable.log_entries") : "log entries"}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={`${logs.filter((l) => l.level === 'error').length} Errors`}
            size="small"
            color="error"
            variant="outlined" />
          
          <Chip
            label={`${logs.filter((l) => l.level === 'warning').length} Warnings`}
            size="small"
            color="warning"
            variant="outlined" />
          
          <Chip
            label={`${logs.filter((l) => l.level === 'info').length} Info`}
            size="small"
            color="info"
            variant="outlined" />
          
        </Box>
      </Box>
    </Box>);

};

export default LogsTable;